/**
 * Effect schemas — the v0 effect grammar (decision D15).
 *
 * @remarks
 * Effects are the only way authored content changes `RuntimeState`;
 * `engine-runtime` applies them through pure transition functions in R5. The
 * union is discriminated on `type` (architecture.md §3.5/§5); parsed output is
 * deep-frozen. Scheduler/ending/puzzle-chain effects are Phase 2/3 variants —
 * the union is built to grow, and every evaluator narrows with `switch` +
 * `never` guards so each new variant breaks compilation at every site that
 * must care.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { type ConditionDefinition, conditionSchema } from './conditions.js';
import { shadowedArmsOf } from './first-match.js';
import {
	actorIdSchema,
	counterIdSchema,
	endingIdSchema,
	eventDefinitionIdSchema,
	exitIdSchema,
	flagIdSchema,
	itemIdSchema,
	promptIdSchema,
	roomIdSchema,
	voiceLineIdSchema,
} from './identifiers.js';
import { nonNegativeIntegerSchema, positiveIntegerSchema } from './primitives.js';
import { narrativeLineFieldSchemas } from './transcript.js';

/**
 * D244: how far an `adjust_counter` moves its counter — any whole step but
 * none. An adjustment of zero says nothing, and a pack that wrote one meant
 * something else; refused here, where the shape alone can judge it.
 */
const counterStepSchema = z.int().refine((step) => step !== 0, { message: 'an adjust_counter moves its counter by a whole step, up or down — never by zero' });

/**
 * Every effect that does one thing — the grammar's leaves (D15), as one
 * list so the schema below and the type beside it read the same variants.
 */
const LEAF_EFFECT_VARIANTS = [
	z.strictObject({
		itemId: itemIdSchema,
		type: z.literal('add_item_to_inventory'),
	}),
	/*
	 * D244: the counter pair, the switch pair's shape for an amount. An
	 * adjustment moves the counter by `by`, a setting puts it at `value`,
	 * and both are CLAMPED to the counter's declared range — the suit's
	 * canister cannot hold thirteen minutes or minus one, whatever the
	 * pack asks. A move that lands on the range's floor or ceiling from
	 * elsewhere fires that counter's `onMin` or `onMax`, in the same pass
	 * (see `counters.ts`). A `value` outside the range is refused at load:
	 * clamping a typo would hide it.
	 */
	z.strictObject({
		by: counterStepSchema,
		counterId: counterIdSchema,
		type: z.literal('adjust_counter'),
	}),
	z.strictObject({
		...narrativeLineFieldSchemas,
		type: z.literal('append_narrative'),
	}),
	/*
	 * D235 (actors round B2; `docs/design/actors.md` §4, Mike's ruling 6):
	 * the GENERAL door to a prompt — the actor asks, because something
	 * decided it should: a trigger on the player's arrival, a topic's
	 * answer, the fall of a foe. `talk to <actor>` opening its `opensWith`
	 * is this effect's sugar. The engine speaks the prompt's question in
	 * the actor's voice and parks it as a `dialogue` question; like
	 * `speak_voice_line`, a declined ask is silent, never an error — the
	 * actor out of earshot, the prompt already asked and not repeatable,
	 * or every option failing its `when`.
	 */
	z.strictObject({
		actorId: actorIdSchema,
		promptId: promptIdSchema,
		type: z.literal('ask_prompt'),
	}),
	/*
	 * D50: the door half of containment. Found by Calder while authoring —
	 * the wall locker is opened by an authored `use pry bar on locker`
	 * interaction, not by bare `open`, and without these the locker could
	 * declare a closed door that nothing in the pack could ever open.
	 *
	 * They mirror `unlock_exit`, which exists for exactly the analogous
	 * reason on the exit side: an authored interaction has to be able to
	 * change the state a later turn reads. `close_container` ships with its
	 * sibling because a door that only opens is half a mechanism, and the
	 * pair costs nothing once the state exists.
	 */
	z.strictObject({
		itemId: itemIdSchema,
		type: z.literal('close_container'),
	}),
	z.strictObject({
		itemId: itemIdSchema,
		type: z.literal('open_container'),
	}),
	/*
	 * D233 (actors round C; `docs/design/actors.md` §5): the ONLY way an
	 * actor's location changes. `destination` is a declared room, or
	 * `'offstage'` — declared and nowhere, which is where an actor with no
	 * `initialRoomId` starts and where one goes when its part is done. The
	 * engine writes `world.actorLocations` and, when the player stands in
	 * the room being left or the room being entered, speaks the actor's
	 * `departureNarratives` / `arrivalNarratives`; an unwitnessed move is
	 * silent, which is how "one room behind you" works. The map does not
	 * draw actors (D33: the map is rooms and provenance).
	 */
	z.strictObject({
		actorId: actorIdSchema,
		destination: z.union([roomIdSchema, z.literal('offstage')]),
		type: z.literal('move_actor'),
	}),
	z.strictObject({
		targetRoomId: roomIdSchema,
		type: z.literal('move_player'),
	}),
	z.strictObject({
		itemId: itemIdSchema,
		type: z.literal('remove_item'),
	}),
	/*
	 * D244 (Act II §13.2, Act I §13.11 carried): the heal — the infirmary's
	 * autodoc. `to: 'full'` puts the player's health back at the
	 * manifest's `player.health`; `by` restores that much, never past it.
	 * Exactly one of the two. A heal in a pack whose player has no body is
	 * refused at load (it could never do anything).
	 */
	z
		.strictObject({
			by: positiveIntegerSchema.optional(),
			to: z.literal('full').optional(),
			type: z.literal('restore_health'),
		})
		.superRefine((effect, context) => {
			if ((effect.by === undefined) === (effect.to === undefined)) {
				context.addIssue({ code: 'custom', message: "a heal says how much: `to: 'full'` or `by: <n>` — exactly one", path: ['to']});
			}
		}),
	/*
	 * R11: how a story concludes. Selects a declared ending — first
	 * selection wins; the epilogue lines are the ending's own. Fireable
	 * from an interaction, an event, or a trigger identically, because
	 * everything that can act can conclude (the apply-effect law: whatever
	 * an action can do, time and watchers can do).
	 */
	z.strictObject({
		counterId: counterIdSchema,
		type: z.literal('set_counter'),
		value: z.int(),
	}),
	z.strictObject({
		endingId: endingIdSchema,
		type: z.literal('set_ending'),
	}),
	z.strictObject({
		flagId: flagIdSchema,
		type: z.literal('set_flag'),
		value: z.boolean(),
	}),
	/*
	 * R12: utter a declared voice line, subject to the LINE'S own policy
	 * (once, cooldown, conditions, pool cursor) — a skipped speak is
	 * silent, never an error. The line owns HOW it speaks; this effect
	 * only says WHEN.
	 */
	z.strictObject({
		type: z.literal('speak_voice_line'),
		voiceLineId: voiceLineIdSchema,
	}),
	/*
	 * D56: the switch pair, mirroring the door pair above for the same
	 * reason — an authored interaction has to be able to move a state a
	 * later turn reads. The breaker bank throwing the substation dark is
	 * this effect, aimed at things the player never touched.
	 */
	/*
	 * R9: the clock's two hands. `schedule_event` arms a declared event
	 * `delayTurns` executed turns from now (0 = this turn's own tick, after
	 * the action's entries); `cancel_event` removes every pending occurrence
	 * of one. They ship together because arm-without-disarm is half a
	 * mechanism — an avertable countdown is the genre's whole point.
	 */
	z.strictObject({
		eventId: eventDefinitionIdSchema,
		type: z.literal('cancel_event'),
	}),
	/*
	 * D142: take a CHECKPOINT — the world as it stands, kept in progression
	 * so an ending declaring `restartAt: 'checkpoint'` can put the player
	 * back there. The general primitive; `room.isCheckpoint` is sugar the
	 * loader desugars into a trigger that fires this every executed turn
	 * in the room. What a restore keeps and what it resets is the engine's
	 * one rule (the world, not the player — see the runtime's
	 * `checkpoint.ts`).
	 *
	 * D270: `wakeRoomId` is where a restore puts the player, when that is not
	 * where the photograph was taken — the Meridian's Drum photographs the
	 * world on Spoke One, the first room every Act II road reaches, and wakes
	 * the player in the infirmary's pod, because the fiction wakes people in
	 * pods. Absent, the player wakes where it was taken, as the room sugar does.
	 */
	z.strictObject({
		type: z.literal('checkpoint'),
		wakeRoomId: roomIdSchema.optional(),
	}),
	z.strictObject({
		delayTurns: nonNegativeIntegerSchema,
		eventId: eventDefinitionIdSchema,
		type: z.literal('schedule_event'),
	}),
	z.strictObject({
		itemId: itemIdSchema,
		type: z.literal('switch_off'),
	}),
	z.strictObject({
		itemId: itemIdSchema,
		type: z.literal('switch_on'),
	}),
	z.strictObject({
		exitId: exitIdSchema,
		type: z.literal('unlock_exit'),
	}),
] as const;

/** An effect that does one thing — every variant but D246's branch. */
type LeafEffectDefinition = Readonly<z.infer<(typeof LEAF_EFFECT_VARIANTS)[number]>>;

/**
 * D246: one arm of a `choose` — a condition and what to do when it holds.
 *
 * @remarks
 * `when` is ONE condition, as a stock draw's is (D244): `all_of` and
 * `any_of` compose. An arm with no `when` is the OTHERWISE — it always
 * holds, so it is the last arm or it shadows the rest. An arm's `effects`
 * may be empty: `{ when: X, effects: [] }` before an otherwise is how a
 * branch says "unless X", and nothing else says it as plainly.
 */
export interface ChooseArm {
	readonly effects: readonly EffectDefinition[];
	readonly when?: ConditionDefinition;
}

/**
 * D246: the branch — the first arm whose `when` holds runs its effects, and
 * the rest do not. No arm holding, and no otherwise, is nothing at all.
 */
export interface ChooseEffect {
	readonly arms: readonly ChooseArm[];
	readonly type: 'choose';
}

/** The v0 effect grammar (D15) — a discriminated union on `type`, the branch (D246) among it. */
export type EffectDefinition = ChooseEffect | LeafEffectDefinition;

/**
 * D246: refuse a branch that can never mean what it says — the shape alone
 * decides each of the three.
 *
 * @remarks
 * - **An arm after the otherwise never runs.** Arms are read first match
 *   wins, and an arm with no `when` always matches — the rule D245 gave the
 *   `use` arms, through the same {@link shadowedArmsOf}.
 * - **One arm with no `when` cannot branch.** It is its effects, written in
 *   the list; the choose around them says nothing.
 * - **The last arm does something.** An empty arm says "not here", which
 *   means something only before an arm that does: an empty otherwise is
 *   what no otherwise already says (D229's one reading, one spelling), and
 *   an empty last `when` arm changes nothing whether it holds or not.
 *
 * @param choose - The parsed branch
 * @param choose.arms - Its arms
 * @param context - Zod's refinement context
 */
const refuseChooseThatCannotMean = ({ arms }: { readonly arms: readonly ChooseArm[] }, context: z.RefinementCtx): void => {
	for (const { armIndex } of shadowedArmsOf(arms, (arm) => arm.when === undefined)) {
		context.addIssue({ code: 'custom', message: 'an earlier arm has no `when` and always runs — arms are read first match wins, so this one could never run', path: ['arms', armIndex]});
	}

	if (arms.length === 1 && arms[0]?.when === undefined) {
		context.addIssue({ code: 'custom', message: 'a choose whose only arm has no `when` cannot branch — write its effects in the list itself', path: ['arms', 0]});
	}

	if (arms.at(-1)?.effects.length === 0) {
		context.addIssue({
			code: 'custom',
			message: 'the last arm does nothing — an empty arm says "not here", which only means something before an arm that does; drop it',
			path: ['arms', arms.length - 1, 'effects'],
		});
	}
};

/**
 * Validates one authored effect of any variant (D15): `add_item_to_inventory`,
 * `adjust_counter` (D244), `append_narrative` (priority defaults to `standard`), `ask_prompt` (D235), `cancel_event`,
 * `choose` (D246), `close_container`, `move_actor` (D233), `move_player`, `open_container`, `remove_item`,
 * `restore_health` (D244), `schedule_event`, `set_counter` (D244), `set_ending`, `set_flag`, `speak_voice_line`,
 * `switch_off`, `switch_on`, `unlock_exit`.
 *
 * @remarks
 * D246 made the grammar RECURSIVE — an arm's effects are effects — so, as
 * with the condition family (see `conditions.ts`), Zod cannot infer the
 * type: {@link EffectDefinition} is written by hand from the leaves' own
 * inference and the branch's interfaces, and the `z.ZodType` annotation
 * keeps the two honest.
 */
export const effectSchema: z.ZodType<EffectDefinition> = z
	.discriminatedUnion('type', [
		...LEAF_EFFECT_VARIANTS,
		z
			.strictObject({
				arms: z
					.array(
						z
							.strictObject({
								effects: z.array(z.lazy(() => effectSchema)).readonly(),
								when: z.lazy(() => conditionSchema).optional(),
							})
							.readonly(),
					)
					.min(1)
					.readonly(),
				type: z.literal('choose'),
			})
			.superRefine(refuseChooseThatCannotMean),
	])
	.readonly();
/** Adds the named item to the player's inventory (removing it from its room, if placed). */
export type AddItemToInventoryEffect = Extract<EffectDefinition, { type: 'add_item_to_inventory' }>;

/** D244: moves the named counter by a whole step, clamped to its declared range. */
export type AdjustCounterEffect = Extract<EffectDefinition, { type: 'adjust_counter' }>;

/** D244: puts the named counter at a value inside its declared range. */
export type SetCounterEffect = Extract<EffectDefinition, { type: 'set_counter' }>;

/** D244: the two counter effects, as one family — every reader that moves an amount reads both. */
export type CounterEffect = AdjustCounterEffect | SetCounterEffect;

/** Appends one structured narrative line to the transcript. */
export type AppendNarrativeEffect = Extract<EffectDefinition, { type: 'append_narrative' }>;

/** D235: the named actor asks the named prompt — the general door to a dialogue question. */
export type AskPromptEffect = Extract<EffectDefinition, { type: 'ask_prompt' }>;

/** D50: shuts the named openable thing's door (the sibling of {@link OpenContainerEffect}). */
export type CloseContainerEffect = Extract<EffectDefinition, { type: 'close_container' }>;

/** D233: puts the named actor in a declared room, or offstage — the only way a body's location changes. */
export type MoveActorEffect = Extract<EffectDefinition, { type: 'move_actor' }>;

/** Where a `move_actor` can put a body: a declared room, or nowhere. */
export type ActorDestination = MoveActorEffect['destination'];

/** Moves the player to the named room (bypassing exits — content-driven teleports/forced moves). */
export type MovePlayerEffect = Extract<EffectDefinition, { type: 'move_player' }>;

/** D50: opens the named openable thing's door, revealing whatever declares itself inside it. */
export type OpenContainerEffect = Extract<EffectDefinition, { type: 'open_container' }>;

/** Removes the named item from the world entirely (inventory or room — consumed, destroyed, taken). */
export type RemoveItemEffect = Extract<EffectDefinition, { type: 'remove_item' }>;

/** D244: restores the player's health — to full, or by an amount, never past the manifest's `player.health`. */
export type RestoreHealthEffect = Extract<EffectDefinition, { type: 'restore_health' }>;

/** Sets the named flag to `value`. */
export type SetFlagEffect = Extract<EffectDefinition, { type: 'set_flag' }>;

/** R11: selects a declared ending — first selection wins; the epilogue is the ending's own. */
export type SetEndingEffect = Extract<EffectDefinition, { type: 'set_ending' }>;

/** R12: utters a declared voice line, subject to the line's own policy. */
export type SpeakVoiceLineEffect = Extract<EffectDefinition, { type: 'speak_voice_line' }>;

/** R9: removes every pending occurrence of the named event (the sibling of {@link ScheduleEventEffect}). */
export type CancelEventEffect = Extract<EffectDefinition, { type: 'cancel_event' }>;
/** The D142 checkpoint effect. */
export type CheckpointEffect = Extract<EffectDefinition, { type: 'checkpoint' }>;

/** R9: arms the named event `delayTurns` executed turns from now. */
export type ScheduleEventEffect = Extract<EffectDefinition, { type: 'schedule_event' }>;

/** D56: switches the named thing OFF (the sibling of {@link SwitchOnEffect}). */
export type SwitchOffEffect = Extract<EffectDefinition, { type: 'switch_off' }>;

/** D56: switches the named thing ON. */
export type SwitchOnEffect = Extract<EffectDefinition, { type: 'switch_on' }>;

/** Marks the named exit unlocked in `WorldState.unlockedExitIds`. */
export type UnlockExitEffect = Extract<EffectDefinition, { type: 'unlock_exit' }>;
